/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Inline Tag & Placeholder Renderer Component (Task 10.5.1)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React from 'react';

interface InlineTagRendererProps {
  text: string;
  onTagClick?: (tag: string) => void;
  className?: string;
}

interface TextToken {
  type: 'text' | 'tag' | 'variable';
  content: string;
}

/**
 * Splits text into plain text tokens, HTML/XML tags (<tag>), and variables ({var}).
 */
export function tokenizeInlineTags(input: string): TextToken[] {
  if (!input) return [];

  // Match HTML tags or variable placeholders: <...> or {...}
  const regex = /(<\/?[a-zA-Z0-9_-]+(?:(?:\s+[\w:-]+(?:\s*=\s*(?:".*?"|'.*?'|[^'">\s]+))?)+\s*|\s*)\/?>|\{+[a-zA-Z0-9_]+\}+)/g;

  const tokens: TextToken[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(input)) !== null) {
    // Add text prior to match
    if (match.index > lastIndex) {
      tokens.push({
        type: 'text',
        content: input.slice(lastIndex, match.index),
      });
    }

    const matchedStr = match[0];
    const isVar = matchedStr.startsWith('{') && matchedStr.endsWith('}');
    tokens.push({
      type: isVar ? 'variable' : 'tag',
      content: matchedStr,
    });

    lastIndex = regex.lastIndex;
  }

  // Add remaining trailing text
  if (lastIndex < input.length) {
    tokens.push({
      type: 'text',
      content: input.slice(lastIndex),
    });
  }

  return tokens;
}

/**
 * Alias for tokenizeInlineTags for test compatibility
 */
export const parseInlineTags = (input: string) => {
  return tokenizeInlineTags(input).map((t) => ({
    type: t.type === 'variable' ? 'var' : t.type,
    value: t.content,
  }));
};

export const InlineTagRenderer: React.FC<InlineTagRendererProps> = ({
  text,
  onTagClick,
  className = '',
}) => {
  const tokens = tokenizeInlineTags(text);

  return (
    <span className={`inline-block leading-relaxed break-words ${className}`}>
      {tokens.map((token, idx) => {
        if (token.type === 'tag') {
          return (
            <button
              key={idx}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onTagClick?.(token.content);
              }}
              title="Click to copy tag to target segment"
              className="inline-flex items-center px-1.5 py-0.5 mx-0.5 rounded text-[11px] font-mono font-medium bg-primary/10 text-primary border border-primary/20 hover:bg-primary/20 hover:border-primary/40 transition-colors select-all align-middle"
            >
              {token.content}
            </button>
          );
        }

        if (token.type === 'variable') {
          return (
            <button
              key={idx}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onTagClick?.(token.content);
              }}
              title="Click to copy variable to target segment"
              className="inline-flex items-center px-1.5 py-0.5 mx-0.5 rounded text-[11px] font-mono font-medium bg-amber-500/10 text-amber-500 border border-amber-500/20 hover:bg-amber-500/20 hover:border-amber-500/40 transition-colors select-all align-middle"
            >
              {token.content}
            </button>
          );
        }

        return <span key={idx}>{token.content}</span>;
      })}
    </span>
  );
};
